import React, { useState, useEffect } from 'react';
import 'react-phone-input-2/lib/style.css';
import PhoneInput from 'react-phone-input-2';
import { ErrorMessage } from '../ErrorMessage';
import { LuAsterisk } from 'react-icons/lu';
import PasswordToggle from '../PasswordToggle';

type InputFieldProps = {
  type?: string;
  id: string;
  className?: string;
  placeholder?: string;
  label?: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  icon?: React.ComponentType;
  max?: number;
  min?: number;
  step?: number;
  error?: string;
  maxLength?: number;
  disabled?: boolean;
  darkMode?: boolean;
};

const InputField: React.FC<InputFieldProps> = ({
  type = 'text',
  id,
  className = '',
  placeholder,
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  required = false,
  icon: Icon,
  max,
  min,
  step,
  error,
  maxLength,
  disabled,
  darkMode = false,
}) => {
  const [isFocused, setIsFocused] = useState(true);

  const handlePhoneChange = (value: string) => {
    const event = { target: { value } } as React.ChangeEvent<HTMLInputElement>;
    onChange(event);
  };

  // Inject custom CSS for dark mode
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'phone-input-fix-styles';
    style.innerHTML = `
      /* Make all inputs have smaller text */
      input, .react-tel-input .form-control {
        font-size: 0.75rem !important;
        line-height: 1rem !important;
      }
      
      /* React tel input fixes */
      .react-tel-input {
        overflow: visible !important;
        position: relative !important;
      }
      
      .react-tel-input .form-control {
        font-size: 0.75rem !important;
        padding-top: 8px !important;
        padding-bottom: 8px !important;
        background-color: ${darkMode ? '#404040' : '#ffffff'} !important;
        border-color: ${darkMode ? '#525252' : '#d1d5db'} !important;
        color: ${darkMode ? '#ffffff' : '#374151'} !important;
        height: auto !important;
      }
      
      .react-tel-input .form-control:focus {
        border-color: #3b82f6 !important;
        outline: none !important;
      }
      
      .react-tel-input .flag-dropdown {
        background-color: ${darkMode ? '#404040' : '#ffffff'} !important;
        border-color: ${darkMode ? '#525252' : '#d1d5db'} !important;
      }
      
      .react-tel-input .selected-flag {
        background-color: ${darkMode ? '#404040' : '#ffffff'} !important;
      }
      
      .react-tel-input .selected-flag:hover {
        background-color: ${darkMode ? '#4a4a4a' : '#f3f4f6'} !important;
      }
      
      /* Country list */
      .react-tel-input .country-list {
        background-color: ${darkMode ? '#262626' : '#ffffff'} !important;
        border: 1px solid ${darkMode ? '#525252' : '#d1d5db'} !important;
        max-height: 200px !important;
        overflow-y: auto !important;
      }
      
      /* Country items */
      .react-tel-input .country-list .country {
        color: ${darkMode ? '#e5e5e5' : '#374151'} !important;
        padding: 8px 12px !important;
        font-size: 0.75rem !important;
      }
      
      .react-tel-input .country-list .country:hover {
        background-color: ${darkMode ? '#404040' : '#f3f4f6'} !important;
      }
      
      .react-tel-input .country-list .country.highlight {
        background-color: ${darkMode ? '#404040' : '#e5e7eb'} !important;
      }
      
      /* Arrow */
      .react-tel-input .flag-dropdown .selected-flag .arrow {
        border-top-color: ${darkMode ? '#9ca3af' : '#6b7280'} !important;
      }
      
      .react-tel-input .flag-dropdown.open .selected-flag .arrow {
        border-bottom-color: ${darkMode ? '#9ca3af' : '#6b7280'} !important;
      }
    `;
    
    const existingStyle = document.getElementById('phone-input-fix-styles');
    if (existingStyle) {
      existingStyle.remove();
    }
    document.head.appendChild(style);
    
    return () => {
      const styleToRemove = document.getElementById('phone-input-fix-styles');
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [darkMode]);

  return (
    <div className={`relative ${className}`} style={{ overflow: 'visible', position: 'relative' }}>
      {type === 'tel' ? (
        <>
          {label && isFocused && (
            <label
              htmlFor={id}
              className={`flex text-xs font-[600] mb-1 transition-colors ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              {required && (
                <span className="text-red-600 top-[2px] relative">
                  <LuAsterisk />
                </span>
              )}
              {label}
            </label>
          )}

          <div style={{ overflow: 'visible', position: 'relative' }}>
            <PhoneInput
              country={'ng'}
              value={value as string}
              onChange={handlePhoneChange}
              disabled={disabled}
              // Removed enableSearch and searchPlaceholder
              inputProps={{
                id,
                onFocus: () => setIsFocused(true),
                onBlur: () => setIsFocused(true),
                autoComplete: 'off',
                placeholder: placeholder || 'Phone number',
              }}
            />
          </div>

          {error && <ErrorMessage message={error} />}
        </>
      ) : (
        <>
          {label && isFocused && (
            <label
              htmlFor={id}
              className={`flex text-xs font-[600] mb-1 transition-colors ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              {required && (
                <span className="text-red-600 top-[2px] relative">
                  <LuAsterisk />
                </span>
              )}
              {label}
            </label>
          )}

          <div className='relative' style={{ overflow: 'visible' }}>
            {Icon && (
              <span className={`absolute p-[5px] rounded-[50px] top-[6px] left-[7px] text-base transition-colors ${
                darkMode ? 'text-gray-400' : 'text-[#98A1B0]'
              }`} style={{ zIndex: 1 }}>
                <Icon />
              </span>
            )}

            <input
              type={type}
              id={id}
              className={`focus:outline-none focus:shadow-outline border rounded-[3px] ${
                Icon ? 'pl-10' : 'pl-3'
              } px-3 py-2 w-full text-xs transition-all duration-200 ${
                darkMode 
                  ? 'bg-neutral-700 border-neutral-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500' 
                  : 'border-gray-400 text-gray-700 placeholder-gray-400 focus:border-[#58595c] focus:ring-1 focus:ring-[#58595c]'
              } ${error && 'border-red-500 dark:border-red-500 focus:ring-red-500'} ${
                disabled && (darkMode ? 'bg-neutral-600 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-500 cursor-not-allowed')
              } ${className}`}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              onBlur={(e) => {
                onChange?.(e);
                onBlur?.(e);
              }}
              onFocus={onFocus}
              max={max}
              min={min}
              step={step}
              autoComplete="off"
              maxLength={maxLength}
              disabled={disabled}
            />

            {type === 'password' && <PasswordToggle targetId={id} darkMode={darkMode} />}

            {error && <ErrorMessage message={error} />}
          </div>
        </>
      )}
    </div>
  );
};

export default InputField;